/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react';
import {
  CURRENCY_VS_SYMBOL_MAP,
  fetcher,
  formatNumberWithUniqueCommaPattern,
  type SerializedTransaction,
} from '@commenda/utils';
import Image from 'next/image';
import { Currency, StockClassType } from '@prisma/client';
import { TrashIcon } from '@heroicons/react/20/solid';
import useModal from '../../../hooks/useModal';
import ConfirmDeleteModal from '../../modal/confirm-delete-modal';

const DeleteSection = ({
  id,
  token,
  mutate,
  onDeleteSuccess,
  onDeleteFailure,
}: {
  id: string;
  token: string;
  mutate: () => void;
  onDeleteSuccess: () => void;
  onDeleteFailure: () => void;
}) => {
  const onDeleteTransaction = async () => {
    const response = await fetcher(
      `capTable/transaction/${id}`,
      { method: 'DELETE' },
      token
    );

    if (!response.ok) {
      onDeleteFailure();
      return false;
    }

    onDeleteSuccess();

    await mutate();

    return true;
  };

  const {
    isShown: isDeleteTransactionModalShown,
    toggle: toggleDeleteTransactionModalVisibility,
  } = useModal();

  return (
    <div>
      <ConfirmDeleteModal
        isDeleteModalShowed={isDeleteTransactionModalShown}
        toggleDeleteModal={toggleDeleteTransactionModalVisibility}
        headerText="Really delete this transaction?"
        modalMessage="This action cannot be reversed."
        onDelete={onDeleteTransaction}
      />
      <TrashIcon
        className="h-6 w-6 text-gray-400 hover:text-red-500 cursor-pointer"
        aria-hidden="true"
        onClick={toggleDeleteTransactionModalVisibility}
      />
    </div>
  );
};

const TransactionsTable = ({
  serializedTransactions,
  token,
  onDeleteFailure,
  onDeleteSuccess,
  mutate,
  currency,
}: {
  serializedTransactions: SerializedTransaction[];
  token: string;
  onDeleteSuccess: () => void;
  onDeleteFailure: () => void;
  mutate: () => void;
  currency: Currency;
}) => {
  if (!serializedTransactions?.length)
    return (
      <div className="flex flex-col items-center gap-2 justify-center mt-32">
        <Image
          className="rounded-full"
          src="/empty_company.svg"
          alt=""
          width={324}
          height={324}
        />
        <h6 className="text-lg font-bold text-gray-400 ">Nothing here yet.</h6>
        <p className="text-gray-400">Equity Transactions will appear here.</p>
      </div>
    );

  return (
    <div className="mt-6 flow-root w-full">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Class
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Stakeholder
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Shares
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Per Share Price
                  </th>
                  <th className="sr-only" scope="col">
                    A
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {serializedTransactions?.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {transaction.date}
                    </td>
                    <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                      {transaction.stockClassType === StockClassType.COMMON
                        ? 'Common'
                        : 'Preferred'}
                    </td>
                    <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                      {transaction.stockClass}
                    </td>
                    <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                      {transaction.stakeholder}
                    </td>
                    <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                      {formatNumberWithUniqueCommaPattern(
                        transaction.noOfShares
                      )}
                    </td>
                    <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                      {`${
                        CURRENCY_VS_SYMBOL_MAP[currency]
                      }${formatNumberWithUniqueCommaPattern(
                        transaction.sharePrice
                      )}`}
                    </td>
                    <td className="pr-8 cursor-pointer hover:text-commenda-blue text-blue-40 font-medium transition">
                      <DeleteSection
                        id={transaction.id}
                        token={token}
                        mutate={mutate}
                        onDeleteSuccess={onDeleteSuccess}
                        onDeleteFailure={onDeleteFailure}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionsTable;
